import {
  type BriqpayAddress,
  type BriqpayCompletedAddress,
  type BriqpaySession,
  type CreatCustomerDTO,
  type Option,
  type OrderAddressDTO,
  type OrderDTO,
  type WithOptional
} from 'ecosystem'

const EMPTY_ADDRESS: OrderAddressDTO = {
  address1: '',
  address2: '',
  country: '',
  mobilePhone: '',
  postalCode: '',
  postalRegion: '',
  attention: '',
  reference: ''
}

const toOrderAddress = (address: BriqpayAddress | undefined): OrderAddressDTO => ({
  address1: address?.streetAddress ?? '',
  address2: address?.streetAddress2 ?? '',
  country: address?.country ?? '',
  mobilePhone: address?.phoneNumber ?? '',
  postalCode: address?.zip ?? '',
  postalRegion: address?.city ?? '',
  attention: `${address?.firstName ?? ''} ${address?.lastName ?? ''}`.trim(),
  reference: ''
})

/**
 * Field-wise merge, preferring the first non-empty value.
 *
 * Briqpay hands back *partial* addresses — a consumer session only ever contains what we
 * PATCHed in, and a carrier may have supplied nothing but a postcode and city while the
 * shopper was still typing. Picking one source wholesale would either discard the
 * postcode Briqpay confirmed or ship an address with no street on it, so each field falls
 * back independently.
 */
const mergeAddresses = (...candidates: (OrderAddressDTO | undefined)[]): OrderAddressDTO =>
  candidates.reduce<OrderAddressDTO>((merged, candidate) => {
    if (!candidate) {
      return merged
    }

    return {
      address1: merged.address1 || candidate.address1 || '',
      address2: merged.address2 || candidate.address2 || '',
      country: merged.country || candidate.country || '',
      mobilePhone: merged.mobilePhone || candidate.mobilePhone || '',
      postalCode: merged.postalCode || candidate.postalCode || '',
      postalRegion: merged.postalRegion || candidate.postalRegion || '',
      attention: merged.attention || candidate.attention || '',
      reference: merged.reference || candidate.reference || ''
    }
  }, EMPTY_ADDRESS)

const firstNonEmpty = (...values: (string | null | undefined)[]) =>
  values.find((value) => !!value?.trim()) ?? ''

/**
 * Read the completed party data off a Briqpay session, backfilled from the SF order.
 *
 * Briqpay only fills `data.billing` / `data.shipping` for modules it actually rendered.
 * Business sessions on the full flow populate both; **consumer sessions are payment-only**
 * and contain nothing but what we PATCHed, which for a carrier branch can be as little as
 * a postcode and city. Downstream this feeds carrier bookings — PostNord rejects an
 * incomplete party with `Invalid indata object EdiInstruction` — so anything Briqpay left
 * blank is taken from the SF order, which our own checkout populated.
 */
export const generateBriqpayAddresses = (
  session: BriqpaySession,
  order?: Option<OrderDTO>
): {
  billingAddress: OrderAddressDTO
  shippingAddress: OrderAddressDTO
  customerInfo: WithOptional<CreatCustomerDTO, 'type'> & {
    fullName: string
    phone: string
    companyName?: string
    cin?: string
  }
} => {
  const billing: BriqpayCompletedAddress | undefined = session.data?.billing
  const shipping: BriqpayCompletedAddress | undefined = session.data?.shipping
  const transaction = session.data?.transactions?.[0]
  const customer = order?.customer

  // Briqpay first (the shopper may have corrected it inside the widget), then whatever
  // our own checkout already recorded on the order.
  const billingAddress = mergeAddresses(
    toOrderAddress(billing),
    order?.billingAddress,
    order?.shippingAddress
  )
  const shippingAddress = mergeAddresses(
    toOrderAddress(shipping),
    order?.shippingAddress,
    order?.billingAddress,
    billingAddress
  )

  const firstName = firstNonEmpty(billing?.firstName, shipping?.firstName, customer?.firstName)
  const lastName = firstNonEmpty(billing?.lastName, shipping?.lastName, customer?.lastName)
  const fullName = firstNonEmpty(
    `${firstName} ${lastName}`.trim(),
    billingAddress.attention,
    shippingAddress.attention
  )

  return {
    billingAddress,
    shippingAddress,
    customerInfo: {
      email: firstNonEmpty(billing?.email, transaction?.email, customer?.email),
      firstName,
      lastName,
      phone: firstNonEmpty(
        billing?.phoneNumber,
        transaction?.phoneNumber,
        billingAddress.mobilePhone,
        shippingAddress.mobilePhone,
        customer?.phones?.[0]?.number
      ),
      fullName,
      // With an orchestrator the buying company can come from either the billing address
      // or the company-lookup module, depending on which modules ran.
      companyName: billing?.companyName ?? session.data?.company?.name,
      cin: billing?.cin ?? session.data?.company?.cin
    }
  }
}
